import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

export const mapCoordinateV1Schema = z.object({
  longitude: z.number().finite().min(-180).max(180),
  latitude: z.number().finite().min(-90).max(90),
}).strict()

const polygon = z.array(mapCoordinateV1Schema).min(3).max(200)

export const propertyAddressResolveV1Schema = z.object({
  addressLine1: z.string().trim().min(1).max(160),
  locality: z.string().trim().min(1).max(100),
  region: z.string().length(2).toUpperCase(),
  postalCode: z.string().regex(/^\d{5}(?:-\d{4})?$/),
  countryCode: z.literal("US"),
  permanent: z.literal(true),
}).strict()

const mapboxProvenance = {
  providerKey: z.literal("mapbox"),
  attribution: z.literal("© Mapbox © OpenStreetMap"),
  retrievedAt: z.iso.datetime({ offset: true }),
}

export const propertyAddressResultV1Schema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("available"),
    reason: z.null(),
    ...mapboxProvenance,
    providerVersion: z.literal("geocoding-v6"),
    featureId: z.string().min(1).max(200),
    formattedAddress: z.string().min(1).max(500),
    coordinate: mapCoordinateV1Schema,
    accuracy: z.string().min(1).max(40).nullable(),
  }).strict(),
  z.object({
    status: z.literal("unavailable"),
    reason: z.enum([
      "no_match",
      "ambiguous",
      "invalid_input",
      "rate_limited",
      "quota_exceeded",
      "provider_unavailable",
      "incompatible",
    ]),
    providerKey: z.literal("mapbox"),
    attribution: z.literal("© Mapbox © OpenStreetMap"),
    retrievedAt: z.iso.datetime({ offset: true }),
    providerVersion: z.literal("geocoding-v6"),
  }).strict(),
])

export const propertyMapConfigurationV1Schema = z.object({
  configurationVersion: z.literal(1),
  providerKey: z.literal("mapbox"),
  styleUrl: z.literal("mapbox://styles/mapbox/satellite-streets-v12"),
  publicAccessToken: z.string().regex(/^pk\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/),
  attribution: z.object({
    mapbox: z.literal("https://www.mapbox.com/"),
    openStreetMap: z.literal("https://www.openstreetmap.org/copyright"),
    improveThisMap: z.literal("https://apps.mapbox.com/feedback/"),
  }).strict(),
  resultStorage: z.literal("permanent"),
}).strict()

export const routeInputsResolveV1Schema = z.object({
  profile: z.enum(["driving", "driving-traffic", "walking", "cycling"]),
  coordinates: z.array(mapCoordinateV1Schema).min(2).max(10),
}).strict()

export const routeInputsResultV1Schema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("available"),
    reason: z.null(),
    ...mapboxProvenance,
    providerVersion: z.literal("directions-v5+matrix-v1"),
    durationsSeconds: z.array(z.array(z.number().int().nonnegative().nullable()).min(2).max(10)).min(2).max(10),
    distancesMeters: z.array(z.array(z.number().int().nonnegative().nullable()).min(2).max(10)).min(2).max(10),
    route: z.object({
      distanceMeters: z.number().int().nonnegative(),
      durationSeconds: z.number().int().nonnegative(),
      geometry: z.array(mapCoordinateV1Schema).min(2).max(10_000),
    }).strict(),
  }).strict(),
  z.object({
    status: z.literal("unavailable"),
    reason: z.enum([
      "invalid_input",
      "no_route",
      "rate_limited",
      "quota_exceeded",
      "provider_unavailable",
      "incompatible",
    ]),
    providerKey: z.literal("mapbox"),
    attribution: z.literal("© Mapbox © OpenStreetMap"),
    retrievedAt: z.iso.datetime({ offset: true }),
    providerVersion: z.literal("directions-v5+matrix-v1"),
  }).strict(),
])

const structureInput = z.object({
  structureId: z.string().min(1).max(120),
  footprintAreaSqFt: z.number().int().nonnegative(),
  footprintSemantic: z.literal("ground_footprint"),
}).strict()

const exclusionInput = z.object({
  exclusionId: entityIdSchema,
  label: z.string().trim().min(1).max(120),
  areaSqFt: z.number().int().nonnegative(),
  polygon: polygon.nullable(),
}).strict()

export const propertyMeasurementRecordV1Schema = z.object({
  measurementId: entityIdSchema,
  serviceLocationId: entityIdSchema,
  replacesMeasurementId: entityIdSchema.nullable(),
  sourceMode: z.enum(["county", "mapbox_polygon", "manual_onsite"]),
  sourceLookupId: entityIdSchema.nullable(),
  sourceRetrievedAt: z.iso.datetime({ offset: true }).nullable(),
  sourceProviderKey: z.string().regex(/^[a-z0-9][a-z0-9._-]{0,79}$/),
  sourceVersion: z.string().trim().min(1).max(120),
  sourceAttribution: z.string().trim().min(1).max(500),
  parcelId: z.string().trim().min(1).max(160).nullable(),
  parcelAreaSqFt: z.number().int().nonnegative().nullable(),
  structures: z.array(structureInput).max(100),
  measurementPolygon: polygon.nullable(),
  exclusions: z.array(exclusionInput).max(50),
  manualAreaSqFt: z.number().int().positive().nullable(),
  verificationState: z.enum(["Draft", "Verified"]),
  overrideReason: z.string().trim().min(2).max(500).nullable(),
}).strict()
  .superRefine((value, context) => {
    if (
      value.sourceMode === "county"
      && (value.sourceLookupId === null || value.parcelAreaSqFt === null)
    ) {
      context.addIssue({
        code: "custom",
        path: ["sourceLookupId"],
        message: "County measurements require a lookup and parcel area.",
      })
    }
    if (value.sourceMode === "mapbox_polygon" && value.measurementPolygon === null) {
      context.addIssue({
        code: "custom",
        path: ["measurementPolygon"],
        message: "Mapbox polygon measurements require a polygon.",
      })
    }
    if (value.sourceMode === "manual_onsite" && value.manualAreaSqFt === null) {
      context.addIssue({
        code: "custom",
        path: ["manualAreaSqFt"],
        message: "Manual measurements require an area.",
      })
    }
    if (
      value.verificationState === "Verified"
      && value.sourceMode !== "county"
      && value.overrideReason === null
    ) {
      context.addIssue({
        code: "custom",
        path: ["overrideReason"],
        message: "Verified fallback measurements require a reason.",
      })
    }
  })

export const propertyMeasurementResultV1Schema = z.object({
  entityType: z.literal("property_measurement"),
  entityId: entityIdSchema,
  state: z.enum(["Draft", "Verified"]),
  version: z.number().int().positive(),
  serviceLocationId: entityIdSchema,
  sourceMode: z.enum(["county", "mapbox_polygon", "manual_onsite"]),
  formulaVersion: z.literal("serviceable-area-v1"),
  baseAreaSqFt: z.number().int().nonnegative(),
  buildingFootprintAreaSqFt: z.number().int().nonnegative(),
  exclusionAreaSqFt: z.number().int().nonnegative(),
  serviceableAreaSqFt: z.number().int().nonnegative(),
  snapshotSha256: z.string().regex(/^[0-9a-f]{64}$/),
}).strict()

export type MapCoordinateV1 = z.infer<typeof mapCoordinateV1Schema>
export type PropertyAddressResolveV1 = z.infer<typeof propertyAddressResolveV1Schema>
export type PropertyAddressResultV1 = z.infer<typeof propertyAddressResultV1Schema>
export type PropertyMapConfigurationV1 = z.infer<typeof propertyMapConfigurationV1Schema>
export type PropertyMeasurementRecordV1 = z.infer<typeof propertyMeasurementRecordV1Schema>
export type PropertyMeasurementResultV1 = z.infer<typeof propertyMeasurementResultV1Schema>
export type RouteInputsResolveV1 = z.infer<typeof routeInputsResolveV1Schema>
export type RouteInputsResultV1 = z.infer<typeof routeInputsResultV1Schema>
